import { useEffect, useState } from 'react'
import { fetchRecap, type Recap } from '../data/recap'
import { authHeaders, fn } from '../auth/supabase'
import { statType } from '../domain/statTypes'
import { formatDate } from '../lib/date'
import type { StatTypeId } from '../domain/types'

/**
 * The Game Recap page.
 *
 * A thin renderer over game_recap(game_id): every number is the server's, so
 * this page and the shareable image cannot disagree. The framing rules from the
 * brief are honoured by what the data does or does not contain -- standouts only
 * arrive when genuinely new, opponent context only when there is something to
 * compare -- so this file mostly decides layout, not meaning.
 */
export function Recap({ gameId, onClose }: { gameId: string; onClose?: () => void }) {
  const [recap, setRecap] = useState<Recap | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [sharing, setSharing] = useState(false)
  const [shareNote, setShareNote] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    setError(null)
    setRecap(null)
    fetchRecap(gameId).then((r) => {
      if (!alive) return
      if (r.ok) setRecap(r.recap)
      else setError(r.error)
    })
    return () => {
      alive = false
    }
  }, [gameId])

  if (error) {
    return (
      <div className="panel">
        <div className="manage__note manage__note--error">Could not load the recap: {error}</div>
        {onClose && (
          <button type="button" className="btn" onClick={onClose}>
            Back
          </button>
        )}
      </div>
    )
  }

  if (!recap) {
    return (
      <div className="boot">
        <div className="boot__spinner" />
      </div>
    )
  }

  /**
   * Share the card as an image.
   *
   * The image is rendered server-side (recap-image) from the same numbers, so it
   * matches the page. On a phone the native share sheet takes a real file --
   * straight into the team chat -- and where that is unavailable (a desktop
   * browser) the image opens in a new tab to save by hand. The picture is
   * fetched with the caller's token, so a signed-out request gets nothing.
   */
  const share = async () => {
    const base = fn('recap-image')
    if (!base) return
    setSharing(true)
    setShareNote(null)
    try {
      const res = await fetch(`${base}?game=${encodeURIComponent(gameId)}`, { headers: await authHeaders() })
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string }
        setShareNote(body.error ?? 'Could not make the image. Try again.')
        return
      }
      const blob = await res.blob()
      const file = new File([blob], `hustle-${g.round_abbreviation ?? 'recap'}.png`, { type: 'image/png' })
      const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean }
      if (nav.canShare?.({ files: [file] })) {
        await nav.share({ files: [file], title: 'Hustle Stats recap' })
      } else {
        // No share sheet (desktop): open it to save manually.
        const url = URL.createObjectURL(blob)
        window.open(url, '_blank')
        setTimeout(() => URL.revokeObjectURL(url), 60_000)
      }
    } catch (err) {
      // A cancelled share sheet throws AbortError; that is not a failure.
      if (!(err instanceof DOMException && err.name === 'AbortError')) {
        setShareNote('Could not share the image.')
      }
    } finally {
      setSharing(false)
    }
  }

  const g = recap.game
  const resultWord = g.result === 'W' ? 'Won' : g.result === 'L' ? 'Lost' : g.result === 'D' ? 'Drew' : ''
  const vs = recap.vs_opponent
  const record =
    vs.played > 1
      ? `${vs.won}-${vs.lost}${vs.drawn ? `-${vs.drawn}` : ''} vs them this season`
      : null

  return (
    <div className="recap">
      <div className="recap__bar">
        {onClose && (
          <button type="button" className="btn btn--sm" onClick={onClose}>
            ← Back
          </button>
        )}
        <button type="button" className="btn btn--sm btn--primary" disabled={sharing} onClick={() => void share()}>
          {sharing ? 'Preparing...' : 'Share image'}
        </button>
      </div>
      {shareNote && <div className="manage__note manage__note--error">{shareNote}</div>}

      <div className="recap__card">
        <header className="recap__header">
          <div className="recap__round">
            {g.round_abbreviation ?? g.round_name ?? 'Game'} · {g.date ? formatDate(g.date) : ''}
          </div>
          <div className="recap__matchup">
            <span className="recap__vs">{g.home_away === 'home' ? 'vs' : '@'}</span>
            <span className="recap__opponent">{g.opponent_name ?? 'Opponent'}</span>
          </div>
          {g.result ? (
            <div className={`recap__result recap__result--${g.result}`}>
              {resultWord} {g.our_score}–{g.their_score}
            </div>
          ) : (
            <div className="recap__result muted">Result not final yet</div>
          )}
          {record && <div className="recap__record muted small">{record}</div>}
        </header>

        {recap.standouts.length > 0 && (
          <div className="recap__standouts">
            {recap.standouts.map((s, i) => (
              <div key={i} className="recap__standout">
                ⭐ {s.text}
              </div>
            ))}
          </div>
        )}

        {recap.awards.length > 0 && (
          <div className="recap__awards">
            {recap.awards.map((a) => (
              <div key={a.stat_type} className="recap__award" style={{ '--stat': colorOf(a.stat_type) } as React.CSSProperties}>
                <div className="recap__award-name">{a.label}</div>
                <div className="recap__award-winner">{a.winners.join(' & ')}</div>
              </div>
            ))}
          </div>
        )}

        {recap.stats.map((block) => (
          <section key={block.stat_type} className="recap__stat">
            <h3 className="recap__stat-title" style={{ color: colorOf(block.stat_type) }}>
              {block.full_name}
              <span className="recap__stat-total">{block.team_total}</span>
            </h3>
            <div className="recap__rows">
              {block.rows.map((r) => (
                <div key={r.name} className={'recap__row' + (r.is_leader ? ' recap__row--leader' : '')}>
                  <span className="recap__name">
                    {r.is_leader && '★ '}
                    {r.name}
                    {r.is_fill_in && <span className="recap__fillin"> fill-in</span>}
                  </span>
                  <span className="recap__count">{r.count}</span>
                </div>
              ))}
            </div>
          </section>
        ))}

        {recap.opponent_context.length > 0 && (
          <section className="recap__context">
            <h3 className="recap__stat-title">Against {g.opponent_name}</h3>
            {recap.opponent_context.map((c) => (
              <div key={c.stat_type} className="recap__context-row">
                <span>{c.full_name}</span>
                <span className="muted small">
                  {c.vs_them}/game vs them · {c.vs_others}/game vs everyone else
                </span>
              </div>
            ))}
          </section>
        )}

        <section className="recap__attendance">
          <h3 className="recap__stat-title">Who played</h3>
          <p className="recap__who">{recap.attendance.scored.join(', ') || '—'}</p>
          {recap.attendance.discrepancy && (
            <p className="muted small">
              PlayHQ's line-up differs
              {recap.attendance.only_scored.length > 0 && `; scored but not listed: ${recap.attendance.only_scored.join(', ')}`}
              {recap.attendance.only_lineup.length > 0 && `; listed but not scored: ${recap.attendance.only_lineup.join(', ')}`}
              .
            </p>
          )}
        </section>
      </div>
    </div>
  )
}

function colorOf(stat: string): string {
  return statType(stat as StatTypeId).color
}
